import React, { useCallback, useState } from "react";
import { Divider, Typography } from "@mui/material";
import Button from "components/atoms/Button";
import * as Styled from "./styles";
import { WEEK_DAYS, WeekDayKey, MassEditWorkingHoursMenuProps } from "./types";
import { useMassEditForm } from "./hooks/useMassEditForm";
import {
  DayRow,
  HolidayRow,
  ConfirmationModal,
  CloseConfirmationModal,
} from "./components";

const MassEditWorkingHoursMenu: React.FC<MassEditWorkingHoursMenuProps> = ({
  handleCloseMassEditWorkingHours,
  massEditData,
}) => {
  const [isCloseConfirmationModalOpen, setIsCloseConfirmationModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const {
    form,
    watchedValues,
    hasChanges,
    hasAnyDaySelected,
    validationError,
    selectedDays,
    handleSubmit,
    onSubmit,
    resetForm,
    handleDayEnabledChange,
    handleDayIsOpenChange,
    handleHolidayEnabledChange,
    handleHolidayIsOpenChange,
  } = useMassEditForm(massEditData, handleCloseMassEditWorkingHours);

  const { control, setValue } = form;

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setIsCloseConfirmationModalOpen(true);
    } else {
      handleCloseMassEditWorkingHours();
    }
  }, [hasChanges, handleCloseMassEditWorkingHours]);

  const handleConfirmClose = useCallback(() => {
    resetForm();
    setIsCloseConfirmationModalOpen(false);
    handleCloseMassEditWorkingHours();
  }, [resetForm, handleCloseMassEditWorkingHours]);

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmationModalOpen(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    setHasAttemptedSubmit(true);

    if (!hasAnyDaySelected || validationError) {
      return;
    }

    handleSubmit(() => {
      setIsConfirmationModalOpen(true);
    })();
  }, [hasAnyDaySelected, validationError, handleSubmit]);

  const handleConfirmSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
    setIsConfirmationModalOpen(false);
  }, [handleSubmit, onSubmit]);

  return (
    <Styled.Container as="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5">Horário de funcionamento</Typography>
      <Typography variant="body2">
        Defina um <strong>horário único</strong> para as áreas selecionadas.
      </Typography>
      <Divider sx={{ borderColor: "black" }} />

      <Styled.TimePickerContainer>
        {(Object.keys(WEEK_DAYS) as WeekDayKey[]).map((dayKey) => (
          <DayRow
            key={dayKey}
            dayKey={dayKey}
            control={control}
            setValue={setValue}
            isEnabled={watchedValues.days[dayKey].enabled}
            isOpen={watchedValues.days[dayKey].isOpen}
            onEnabledChange={handleDayEnabledChange}
            onIsOpenChange={handleDayIsOpenChange}
          />
        ))}
      </Styled.TimePickerContainer>

      <HolidayRow
        control={control}
        setValue={setValue}
        isEnabled={watchedValues.holiday.enabled}
        isOpen={watchedValues.holiday.isOpen}
        onEnabledChange={handleHolidayEnabledChange}
        onIsOpenChange={handleHolidayIsOpenChange}
      />

      {hasAttemptedSubmit && validationError && (
        <Styled.ErrorMessage>{validationError}</Styled.ErrorMessage>
      )}

      <Styled.ButtonsContainer>
        <Button
          type="button"
          variant="outlined"
          color="inherit"
          onClick={handleClose}
        >
          Fechar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApplyClick}
          disabled={!hasAnyDaySelected}
        >
          Aplicar
        </Button>
      </Styled.ButtonsContainer>

      <CloseConfirmationModal
        open={isCloseConfirmationModalOpen}
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        selectedAreas={massEditData}
      />

      <ConfirmationModal
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleConfirmSubmit}
        selectedAreas={massEditData}
        selectedDays={selectedDays}
      />
    </Styled.Container>
  );
};

export default MassEditWorkingHoursMenu;
